import {shallowMemoize} from '@sail/utils';
import ListPattern from 'cldr-listpattern';
import createGlobalizeWithLocale from 'src/internal/createGlobalizeWithLocale/createGlobalizeWithLocale';
import handleGlobalizeError from 'src/internal/errors/handleGlobalizeError/handleGlobalizeError';
import mergeOptions from 'src/internal/mergeOptions';

import type {IReports} from '@sail/observability';
import type {ReactElement, ReactNode} from 'react';
import type {
  FormatListItem,
  FormatListItemReact,
  FormatListItemText,
  FormatListOptions,
  FormatListOptionsDefaults,
  FormatListOptionsWithDefaults,
  FormatListType,
} from 'src/public/formatList/types';
import type {IntlConfig, Locale} from 'src/types';

const intlTypeToCldrPatternType: Record<
  FormatListType,
  'standard' | 'or' | 'unit'
> = {
  conjunction: 'standard',
  disjunction: 'or',
  unit: 'unit',
};

const splitRegExp = /%%%/g;

const getFormatter = shallowMemoize(
  (config: IntlConfig, locale: Locale, reports: IReports) => {
    try {
      const globalize = createGlobalizeWithLocale(
        config,
        locale,
        undefined,
        reports,
      );
      const listPatternData = globalize.cldr.main('listPatterns');

      return new ListPattern(listPatternData);
    } catch (error) {
      handleGlobalizeError(error, reports);
    }
  },
);

const getPatternType = (options: FormatListOptionsWithDefaults): string => {
  const cldrType = intlTypeToCldrPatternType[options.type];

  const patternType: string[] = [cldrType];

  if (options.style !== 'long') {
    patternType.push(options.style);
  }

  return patternType.filter(Boolean).join('-');
};

const format = (
  items: FormatListItemText[],
  options: FormatListOptionsWithDefaults,
  config: IntlConfig,
  reports: IReports,
): string => {
  const formatter = getFormatter(config, options.locale, reports);

  return formatter(items, {
    patternType: getPatternType(options),
  });
};

function isFormatListItemText(item: unknown): item is FormatListItemText {
  return typeof item === 'string' || typeof item === 'number';
}

function formatList(
  /** The intl config object containing the locale data. When using this API through the `useIntl` hook, this config object is automatically injected. */
  config: IntlConfig,
  reports: IReports,
  /** A collection of list items to be formatted. */
  items: FormatListItemText[],
  /** Options for specifying the type and style of list items grouping */
  options?: FormatListOptions,
): string;
function formatList(
  /** The intl config object containing the locale data. When using this API through the `useIntl` hook, this config object is automatically injected. */
  config: IntlConfig,
  reports: IReports,
  /** A collection of list items to be formatted. */
  items: FormatListItemReact[],
  /** Options for specifying the type and style of list items grouping */
  options?: FormatListOptions,
): ReactElement;
/**
 * Formats a list of primitive values (string | number) or React nodes into a human-readable list,
 * using the appropriate separator for the current locale.
 *
 * E.g. ['lions', 'tigers', 'bears'] => 'lions, tigers, and bears'
 *
 * @see https://sail.stripe.me/apis/intl/useIntl/#format-list
 */
function formatList(
  /** The intl config object containing the locale data. When using this API through the `useIntl` hook, this config object is automatically injected. */
  config: IntlConfig,
  reports: IReports,
  items: FormatListItem[],
  options?: FormatListOptions,
): string | ReactElement {
  const defaults: FormatListOptionsDefaults = {
    type: 'conjunction',
    style: 'long',
    locale: config.locale,
  };

  const mergedOptions: FormatListOptionsWithDefaults = mergeOptions(
    config,
    defaults,
    reports,
    options,
  );

  if (items.every(isFormatListItemText)) {
    return format(items, mergedOptions, config, reports);
  }

  const ids = items.map((_, idx) => `%%%${idx}%%%`);
  const formattedIdList = format(ids, mergedOptions, config, reports);
  const splitList = `${formattedIdList}`.split(splitRegExp);

  const chunks = splitList.reduce<ReactNode[]>((acc, listPart, index) => {
    /* eslint-disable react/no-array-index-key */
    // The ID placeholders are always odd indexes
    if (index % 2 === 1) {
      acc.push(<span key={index}>{items[parseInt(listPart, 10)]}</span>);
    } else if (listPart !== '') {
      acc.push(<span key={index}>{listPart}</span>);
    }
    /* eslint-enable react/no-array-index-key */

    return acc;
  }, []);

  return <>{chunks}</>;
}

export type FormatList = typeof formatList;

export default formatList;
